import React, {
  FunctionComponent,
  useContext,
  MouseEvent,
  useEffect,
  useState
} from 'react';
import { useAppSelector } from 'hooks/redux';
import { EventModel } from '../../EventModel';
import { liveEventSelector } from '../../eventsSelectors';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Icon } from 'components/Icons';
import { useCurrentEvent, useIsEventLive } from '../../eventHooks';
import { ThumbImage } from 'components/Images/ThumbImage';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useAudio } from 'features/audio/audio';
import tinycolor from 'tinycolor2';
import { useDispatch } from 'react-redux';
import { WebSocketContext } from 'features/hub/hub';
import { navigateToEvent } from 'features/events/thunks';

export const LiveBanner: FunctionComponent = () => {
  const liveEvent: EventModel | null = useAppSelector(liveEventSelector);
  const event = useCurrentEvent();
  const currentEventIsLive = useIsEventLive(event);
  const { pathname } = useLocation();
  const isEventPath = matchPath('/events/:id', pathname);
  const { isCreatorAppWebview, isBrowserEmbed, isListenerAppWebview } =
    useContext(UserAgentContext);
  const navigate = useNavigate();
  const { stop, loadAudio } = useAudio();
  const [colorContrastClass, setColorContrastClass] = useState<string>('');
  const channelFallbackEnabled = useAppSelector(
    (state) => state.channel.channelDetails?.fallbackEnabled
  );
  const dispatch = useDispatch();
  const { subscribeToBroadcast } = useContext(WebSocketContext);

  const navigateToLiveEvent = (event: MouseEvent) => {
    event.preventDefault();
    dispatch(
      navigateToEvent(
        liveEvent,
        loadAudio,
        stop,
        navigate,
        subscribeToBroadcast,
        isListenerAppWebview
      )
    );
  };

  useEffect(
    function setCardColorContrast() {
      if (liveEvent) {
        const color = tinycolor(liveEvent.color);
        const colorIsLight = color.getLuminance() > 0.5;
        setColorContrastClass(
          colorIsLight ? 'light-contrast' : 'dark-contrast'
        );
      }
    },
    [liveEvent, setColorContrastClass]
  );

  if (liveEvent?.id === event?.id) {
    return null;
  }

  if (!liveEvent || !isEventPath || isCreatorAppWebview || isBrowserEmbed) {
    return null;
  }

  if (channelFallbackEnabled && currentEventIsLive && liveEvent.fallback) {
    return null;
  }

  if (channelFallbackEnabled && !event?.isUpcoming()) {
    return null;
  }

  return (
    <Link
      to={`/events/${liveEvent.id}`}
      onClick={navigateToLiveEvent}
      className={`${colorContrastClass} tw-bg-theme-color tw-text-white hover:tw-text-white [.light-contrast_&]:tw-text-black tw-max-w-[95%] [.embed_&]:tw-max-w-[280px] sm:tw-max-w-lg tw-w-full tw-p-2 [.ios_&]:tw-p-1 tw-bg-black tw-backdrop-blur-md tw-bg-opacity-80 tw-flex tw-gap-4 tw-font-bold tw-text-base [.ios_&]:tw-text-xs tw-text-white hover:tw-text-white tw-rounded-2xl [.ios_&]:tw-rounded-xl tw-top-[68px] md:tw-top-4 [.embed_&]:tw-top-[48px] tw-fixed tw-left-1/2 tw--translate-x-1/2 tw-z-[1000] [.ios-listener_&]:tw-top-[calc(60px+var(--sat))] [.android-listener_&]:tw-top-[calc(60px+var(--sat))]`}
      style={{ backgroundColor: liveEvent.color }}
    >
      <div className="tw-rounded-md tw-overflow-hidden tw-h-[48px] tw-w-[48px] [.embed_&]:tw-h-12 [.embed_&]:tw-w-12 tw-relative [&>img]:tw-h-full [&>img]:tw-w-full [&>img]:tw-object-cover">
        <ThumbImage
          artworkUrl={liveEvent.artwork.image.small}
          artworkMode={liveEvent.artworkMode}
        />
      </div>
      <div className="tw-flex-1">
        <div className="tw-flex tw-gap-2 tw-uppercase tw-items-center tw-opacity-50">
          <Icon
            icon={'liveNowAnimated'}
            height={12}
            hidden={true}
            label={'One Air'}
            width={12}
          />
          <span>{liveEvent.isFallback() ? 'Fallback' : 'Live now'}</span>
        </div>
        <p className="tw-text-xl [.ios_&]:tw-text-sm tw-font-medium tw-line-clamp-2 [.embed_&]:tw-line-clamp-1 tw-m-0">
          {liveEvent.title}
        </p>
      </div>
      <div className="tw-flex-none tw-flex tw-items-center tw-text-mixlr-red">
        <div className="tw-rounded-full tw-text-white tw-h-[24px] tw-w-[24px] tw-flex tw-items-center tw-justify-center tw-bg-white/20">
          <Icon
            icon={'play'}
            height={12}
            hidden={true}
            label={'Go to live event'}
            width={11}
          />
        </div>
      </div>
    </Link>
  );
};
